import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const CHARS_PER_TICK = 3;
const TICK_MS = 12;

interface StreamingMarkdownProps {
  text: string;
  animate: boolean;
  onComplete?: () => void;
  onTick?: () => void;
}

export const StreamingMarkdown = ({
  text,
  animate,
  onComplete,
  onTick,
}: StreamingMarkdownProps) => {
  const [displayedLength, setDisplayedLength] = useState(
    animate ? 0 : text.length,
  );
  const completedRef = useRef(!animate);
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  useEffect(() => {
    if (!animate || completedRef.current) return;

    const timer = setInterval(() => {
      setDisplayedLength((prev) => {
        const next = prev + CHARS_PER_TICK;
        onTickRef.current?.();
        if (next >= text.length) {
          clearInterval(timer);
          completedRef.current = true;
          onComplete?.();
          return text.length;
        }
        return next;
      });
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [animate, text, onComplete]);

  useEffect(() => {
    if (!animate) {
      setDisplayedLength(text.length);
    }
  }, [text, animate]);

  const visibleText = text.slice(0, displayedLength);
  const isStreaming = animate && displayedLength < text.length;

  return (
    <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-headings:my-2.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0 text-gray-800">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{visibleText}</ReactMarkdown>
      {isStreaming && (
        <span className="inline-block w-1.5 h-4 bg-gray-400 rounded-sm animate-pulse ml-0.5 align-text-bottom" />
      )}
    </div>
  );
};
